import { Placement } from "@popperjs/core";
import React, { forwardRef } from "react";
import { FlyoutArrowProps } from "../../flyout";
import { TooltipFlyoutArrowStyled } from "../styled/TooltipFlyoutArrowStyled";
import { ArrowEdge, DirectionOrAuto } from "../types";
import { Arrows, ArrowsProps } from "./Arrows";

interface TooltipFlyoutArrowProps extends FlyoutArrowProps {
    arrowAtEdge: ArrowEdge;
}

export const TooltipFlyoutArrow = forwardRef<HTMLDivElement, TooltipFlyoutArrowProps>(
    ({ arrowAtEdge, ...props }, ref) => {
        return (
            <TooltipFlyoutArrowStyled {...props} ref={ref}>
                <Arrows
                    align={getAlign(props.placement, arrowAtEdge)}
                    color={props.backgroundColor}
                    rotation={getRotation(props.placement)}
                />
            </TooltipFlyoutArrowStyled>
        );
    }
);
TooltipFlyoutArrow.displayName = "TooltipFlyoutArrow";

// This is a mapping of {actual arrow placement}-{arrowAtEdge} => the kind of arrow alignment we
// should choose. Note that all the combinations are not listed here, only the ones that are
// actually possible. For example, you can't have a "top-top" or "top-bottom" arrow placement.
const arrowAlignLookup: Record<string, "left" | "right"> = {
    "top-left": "left",
    "top-right": "right",
    "left-top": "right",
    "left-bottom": "left",
    "right-top": "left",
    "right-bottom": "right",
    "bottom-left": "right",
    "bottom-right": "left",
};

function getAlign(placement: Placement, arrowAtEdge: ArrowEdge): ArrowsProps["align"] {
    const dir = placement.split("-")[0] as DirectionOrAuto;

    const arrowKey = `${dir}-${arrowAtEdge || "false"}` as const;

    return arrowAlignLookup[arrowKey] ?? "center";
}

function getRotation(placement: Placement) {
    switch (placement) {
        case "top":
        case "top-start":
        case "top-end":
            return 0;
        case "right":
        case "right-start":
        case "right-end":
            return 90;
        case "bottom":
        case "bottom-start":
        case "bottom-end":
            return 180;
        case "left":
        case "left-start":
        case "left-end":
        default:
            return 270;
    }
}
